import { Dispatch, FC, SetStateAction, Suspense, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Dialog } from 'primereact/dialog';
import { usePostHog } from '@posthog/react';
import { v4 as uuid } from 'uuid';
import { exitSlipRegions } from '../../learnosity-regions/ExitSlipRegions';
import { getItemReferences } from '../../bffApiCalls/getItemReferences';
import { useIndividualReportInitialization } from '../../custom-hooks/useIndividualReportInitialization';
import ExitSlipPrintModalWrapper from '../ExitSlip/ExitSlipPrintModalWrapper';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import { ExitSlipModalData } from './IndividualReportDailyActivity';
import { isAbortError } from '../../utils/fetchCancellation';
import CustomErrorBoundary from '../LayoutWrapper/CustomErrorBoundary';
import { ErrorBoundaryUI } from '../ErrorBoundary/ErrorBoundaryUI';
import { LearnosityReportAccessDeniedError } from '../../utils/learnosityErrors';
import { IndividualReportAccessDenied } from './IndividualReportAccessDenied';

interface Props {
  modalData: ExitSlipModalData;
  isVisible: boolean;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
}

const loadErrorMessage = (
  <p role="alert" className="tw-text-center tw-text-base tw-text-neutral-700 tw-py-10">
    We couldn&apos;t load this exit ticket. Please close this window and try again.
  </p>
);

interface ReviewProps {
  modalData: ExitSlipModalData;
  itemReferences: string[];
}

const ExitSlipReviewPlayer: FC<ReviewProps & { fetchKey: string }> = ({ modalData, itemReferences, fetchKey }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [failure, setFailure] = useState<unknown>();

  const variables = useMemo(
    () => ({
      skip: !modalData.publicUserId,
      initModel: {
        userId: modalData.publicUserId ?? '',
        sessionId: modalData.sessionId ?? '',
        activityId: modalData.activityId ?? '',
        title: 'Exit Slip View',
        state: 'review',
        renderingType: 'inline',
        regions: JSON.stringify(exitSlipRegions),
        showCorrectAnswers: true,
        showDistractorRationale: true,
        responsiveRegions: true,
        autoSaveAnswers: false,
        showIntro: false,
        scrollToTop: false,
        scrollToTest: false,
        skipSubmitConfirmation: false,
        warningOnChange: false,
      },
    }),
    [modalData.publicUserId, modalData.sessionId, modalData.activityId],
  );
  const { initialization, refetchInitialization, initializationError } = useIndividualReportInitialization(
    variables,
    fetchKey,
  );

  if (
    initializationError instanceof LearnosityReportAccessDeniedError ||
    failure instanceof LearnosityReportAccessDeniedError
  ) {
    return <IndividualReportAccessDenied />;
  }

  if (!initialization || loadError) {
    return loadErrorMessage;
  }

  return (
    <>
      {loading && <ThreeDotLoader dark />}
      <ExitSlipPrintModalWrapper
        request={initialization}
        refetchInitialization={refetchInitialization}
        onFailure={error => {
          setLoading(false);
          setLoadError(true);
          setFailure(error);
        }}
        setLoading={setLoading}
        itemRefs={itemReferences}
      />
    </>
  );
};

const ExitSlipReviewOpening: FC<ReviewProps> = ({ modalData, itemReferences }) => {
  const [fetchKey] = useState(() => uuid());
  return (
    <Suspense fallback={<ThreeDotLoader dark />}>
      <ExitSlipReviewPlayer modalData={modalData} itemReferences={itemReferences} fetchKey={fetchKey} />
    </Suspense>
  );
};

const IndividualReportDailyExitSlipModalWrapper: FC<Props> = ({ modalData, setIsVisible, isVisible }) => {
  const [itemReferences, setItemReferences] = useState<string[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [maskStyle, setMaskStyle] = useState<number>(1);

  const { getAccessTokenSilently, logout } = useAuth0();
  const posthog = usePostHog();

  useEffect(() => {
    setItemReferences(null);
    setLoadError(false);
    if (!isVisible || !modalData.sessionId || !modalData.activityId) return;
    const controller = new AbortController();

    getItemReferences(modalData.activityId, getAccessTokenSilently, logout, posthog, { signal: controller.signal })
      .then(itemRef => {
        if (controller.signal.aborted) return;
        if (!itemRef?.length) {
          setLoadError(true);
          return;
        }
        setItemReferences(itemRef);
        setMaskStyle(4);
      })
      .catch(err => {
        if (controller.signal.aborted || isAbortError(err)) return;
        console.log('Error fetching item references', err);
        setLoadError(true);
      });
    return () => controller.abort();
  }, [modalData.sessionId, modalData.activityId, modalData.publicUserId, isVisible]);

  const headerTemplate = (
    <div className="tw-flex tw-flex-col">
      <h4>{modalData.title}</h4>
    </div>
  );

  return (
    <Dialog
      visible={isVisible}
      header={headerTemplate}
      style={{
        width: '70vw',
        height: '70vh',
        maxHeight: 'inherit',
        maxWidth: 'inherit',
        zIndex: 5,
      }}
      onHide={() => setIsVisible(false)}
      maskStyle={{ zIndex: maskStyle }}
      className={`nc_exit-slip-print-modal`}
    >
      {loadError && loadErrorMessage}
      {!loadError && !itemReferences?.length && <ThreeDotLoader dark />}
      {isVisible && !loadError && !!itemReferences?.length && (
        <CustomErrorBoundary
          key={JSON.stringify([modalData.publicUserId, modalData.activityId, modalData.sessionId])}
          fallback={<ErrorBoundaryUI embedded />}
        >
          <ExitSlipReviewOpening modalData={modalData} itemReferences={itemReferences} />
        </CustomErrorBoundary>
      )}
    </Dialog>
  );
};

export default IndividualReportDailyExitSlipModalWrapper;
